import { Injectable } from '@angular/core';

/**
 * Tolerance before the device clock is considered wrong. The OIDC library rejects an id_token whose
 * `exp` is past according to the *local* clock, with a built-in tolerance of 10 minutes, so anything
 * beyond a couple of minutes is already worth reporting even when login still succeeds: every date
 * captured offline (start/end of a training, badge reads) is stamped with this clock.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 120_000;

/**
 * Tracks how far the device clock is from the server clock, measured from the `Date` response header
 * of any backend call. A wrong device clock breaks the login round-trip in a way that looks like a
 * server problem (the token is valid, the device just believes it is expired), so having the actual
 * numbers is what makes the diagnosis obvious.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceClockService {
  /** serverTime - deviceTime, in milliseconds. Positive means the device is behind the server. */
  private offsetMs: number | null = null;

  /**
   * Feeds a server `Date` header. Requires the header to be exposed via CORS
   * (`WithExposedHeaders("Date")` in the host module), otherwise it is simply never called.
   */
  registerServerDate(dateHeader: string | null): void {
    if (!dateHeader) {
      return;
    }

    const serverTime = Date.parse(dateHeader);
    if (Number.isNaN(serverTime)) {
      return;
    }

    // The header has a one-second resolution and the response took some time to arrive, so small
    // values are noise. Only meaningful drift is worth keeping.
    this.offsetMs = serverTime - Date.now();
  }

  /** Null while no server response has been seen yet. */
  getOffsetMs(): number | null {
    return this.offsetMs;
  }

  isOutOfSync(toleranceMs: number = CLOCK_SKEW_TOLERANCE_MS): boolean {
    return this.offsetMs !== null && Math.abs(this.offsetMs) > toleranceMs;
  }

  /** Server time as the device could know it, or null when no response has been seen yet. */
  getServerTime(): Date | null {
    return this.offsetMs === null ? null : new Date(Date.now() + this.offsetMs);
  }

  /**
   * Human-readable drift of the device relative to the server: `+` means the device runs ahead,
   * `-` means it runs behind. E.g. a device 12 hours ahead reads "+12 h 0 min".
   */
  describeOffset(): string {
    if (this.offsetMs === null) {
      return '';
    }

    const totalMinutes = Math.round(Math.abs(this.offsetMs) / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const amount = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;

    return this.offsetMs < 0 ? `+${amount}` : `-${amount}`;
  }
}
